import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { Button } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { swSettings } from "app/common/services/swSettings";
import { useMonitorAndProtectCampaigns } from "app/pages/digital-marketing/monitor-and-protect/services/api/campaign";
import { homepageTracking } from "app/pages/digital-marketing/monitor-and-protect/services/tracking/homepage";
import { DomainSearch } from "app/pages/digital-marketing/monitor-and-protect/shared/components/forms/domain-search";
import { usePreloadedHook } from "app/pages/digital-marketing/monitor-and-protect/shared/hooks/preloaded-hook";
import { AssetsService } from "app/services/AssetsService";
import { isNoTouchOrAfterTrial } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";
import { useTranslation } from "pro-features/components/WithTranslation";

const headerImageUrl = AssetsService.assetUrl("/images/secondary-home-page-header-light.png");

const HeaderContainer = styled(Stack)`
    background-image: url("${headerImageUrl}");
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
`;

const Title = styled.h1`
    font-family: "DM Sans", serif;
    font-size: 36px;
    font-weight: 700;
    line-height: 40px;
    color: ${colorsPalettes.carbon["500"]};
`;

interface IHomepageHeaderProps {
    selectedDomain: string;
    onDomainSelect: (domain: string) => void;
    onGetStarted: () => void;
}

export const HomepageHeader = ({ selectedDomain, onDomainSelect, onGetStarted }: IHomepageHeaderProps) => {
    const translate = useTranslation();
    const { isReady, openHook } = usePreloadedHook();
    const { data: campaigns } = useMonitorAndProtectCampaigns({ includeDemoCampaigns: false });

    const isCampaignsLimitReached = () => {
        const campaignsLimit = swSettings.components.MonitorAndProtectMaster.resources.MaximumAccountCampaigns;
        if (campaignsLimit === -1) return false;
        return (campaigns?.length ?? 0) >= campaignsLimit;
    };

    const handleGetStartedClick = () => {
        if (isReady && swSettings.components.MonitorAndProtectMaster.resources.MaximumAccountCrawls === 0) {
            if (isNoTouchOrAfterTrial(swSettings)) {
                openHook("monitorAndProtectNoTouch", "v6");
            } else {
                openHook("MonitorAndProtectUnlock", "v5");
            }
            return;
        }

        if (isReady && isCampaignsLimitReached()) {
            openHook("MonitorAndProtectUpgrade");
            return;
        }

        if (selectedDomain) {
            homepageTracking.onGetStartedClick();
            onGetStarted();
        }
    };

    return (
        <HeaderContainer
            width="100%"
            height="292px"
            alignItems="center"
        >
            <Stack
                height="100%"
                width="804px"
                gap="40px"
                justifyContent="flex-end"
                padding="0 0 24px 0"
            >
                <Stack gap="8px">
                    <Title>{translate("monitor_and_protect.homepage.header.title")}</Title>
                </Stack>

                <Stack gap="8px">
                    <Text
                        as="h6"
                        fontVariant="h6"
                        color={colorsPalettes.carbon["500"]}
                        dangerouslySetInnerHTML={{
                            __html: translate("monitor_and_protect.homepage.header.description_ga"),
                        }}
                    />
                    <Flex
                        gap="16px"
                        alignItems="center"
                    >
                        <DomainSearch
                            placeholder={translate("monitor_and_protect.homepage.header.search_placeholder")}
                            value={selectedDomain}
                            onSelect={onDomainSelect}
                            onSearchBarClick={homepageTracking.onSearchBarClick}
                            onSearchResultClick={homepageTracking.onSearchResultClick}
                        />
                        <Button
                            isDisabled={selectedDomain === ""}
                            onClick={handleGetStartedClick}
                        >
                            {translate("monitor_and_protect.homepage.header.get_started_button")}
                        </Button>
                    </Flex>
                </Stack>
            </Stack>
        </HeaderContainer>
    );
};
